import { Section } from "../../../../components/Section";
import { useRef, useState } from "react";
import { DocumentTable } from "./DocumentTable";
import { Show } from "shared/components";
import { RowSelectionState } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "../../../../../../api/apiBase";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../../utils/error";
import { DeleteDocumentModal } from "./DeleteDocumentModal";
import { Loader } from "shared/components/Loader";

export const DocumentSection = ({ applicantId }: { applicantId: number }) => {
  const queryClient = useQueryClient();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [selectedRentersRow, setSelectedRentersRow] =
    useState<RowSelectionState>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryFn: () =>
      instance
        .get(`/portal/v1/applicants/${applicantId}/additional_documents`)
        .then(({ data }) => data),
    queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
  });

  const handleDownload = (files: { url: string; name: string }[]) => {
    files.forEach(({ url, name }) => {
      fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          Origin: window.location.origin
        }
      })
        .then((response) => response.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = name;
          document.body.appendChild(link);
          link.click();
          URL.revokeObjectURL(blobUrl);
          link.remove();
        });
    });
  };

  const handlePrint = async (url: string) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          Origin: window.location.origin
        }
      });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], {
          type: response.headers.get("Content-Type") || "application/pdf"
        })
      );
      if (iframeRef.current) {
        iframeRef.current.src = blobUrl;
        iframeRef.current.onload = () => {
          iframeRef.current?.contentWindow?.focus();
          iframeRef.current?.contentWindow?.print();
        };
      }
    } catch (error) {
      toast.error((error as Error).message || "Something went wrong");
    }
  };

  const { mutate: deleteDoc } = useMutation({
    mutationKey: ["DELETE_ADDITIONAL_DOCUMENTS", applicantId],
    mutationFn: (docId: number) => {
      return instance.delete(
        `/portal/v1/applicants/${applicantId}/additional_documents/${docId}`
      );
    },
    onError(error) {
      toast.error(getErrorMessage(error) || "Something went wrong");
    },
    onSuccess: () => {
      toast.success("Success! Additional document were successfully deleted.");
      setSelectedRentersRow({});
      queryClient.invalidateQueries({
        queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
      });
    }
  });

  const { mutate: bulkDeleteDocs } = useMutation({
    mutationKey: ["DELETE_ADDITIONAL_DOCUMENTS", applicantId],
    mutationFn: (docIds: number[]) => {
      return instance.delete(
        `/portal/v1/applicants/${applicantId}/additional_documents/bulk_delete`,
        {
          data: { document_ids: docIds }
        }
      );
    },
    onError(error) {
      toast.error(getErrorMessage(error) || "Something went wrong");
    },
    onSuccess: () => {
      toast.success("Success! Additional documents were successfully deleted.");
      setSelectedRentersRow({});
      queryClient.invalidateQueries({
        queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
      });
    }
  });

  if (isLoading)
    return (
      <div className="flex justify-center">
        <Loader size={25} />
      </div>
    );

  const documents = data?.items || [];

  return (
    <>
      <Section
        sectionTitle="Uploaded documents"
        maxWidth="100%"
        rightElement={
          <Show if={Boolean(Object.keys(selectedRentersRow).length)}>
            <div className="flex">
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.TEXT}
                className="cursor-pointer border-r pr-3"
                onClick={() => {
                  handleDownload(
                    Object.keys(selectedRentersRow).map((docId) => {
                      const { asset_url, asset_name } = documents.find(
                        ({ id }) => id === Number(docId)
                      );
                      return { url: asset_url, name: asset_name };
                    })
                  );
                  setSelectedRentersRow({});
                }}
              >
                Download
              </Button>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.TEXT}
                className="cursor-pointer pl-3"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </div>
          </Show>
        }
      >
        <Show if={isError}>
          <div className="flex justify-center">
            <h3 className="text-content-1000 font-medium my-2">
              Error loading additional documents
            </h3>
          </div>
        </Show>
        <Show if={!isError}>
          {documents.length ? (
            <DocumentTable
              documents={documents}
              selectedRentersRow={selectedRentersRow}
              setSelectedRentersRow={setSelectedRentersRow}
              onDelete={(docId) => deleteDoc(docId)}
              onDownload={handleDownload}
              onPrint={handlePrint}
            />
          ) : (
            <p className="text-sm text-center text-content-400 p-6">
              There is no uploaded documents
            </p>
          )}
        </Show>
      </Section>
      <DeleteDocumentModal
        opened={showDeleteModal}
        title="Are you sure you want to delete these documents?"
        onClose={() => setShowDeleteModal(false)}
        onDelete={() =>
          bulkDeleteDocs(Object.keys(selectedRentersRow).map(Number))
        }
      />
      <iframe className="hidden" ref={iframeRef} />
    </>
  );
};
